import * as THREE from "three";
import {
  WORLD,
  PALETTE,
  paletteAt,
  heightT,
  detectTier,
  prefersReducedMotion,
  isWebGLAvailable
} from "./config.js";
import { buildTree, buildMotes } from "./tree.js";
import { buildOrnaments } from "./ornaments.js";
import { buildUnderworld } from "./underworld.js";
import { layoutWorks, buildWorks } from "./works.js";
import { createPanel } from "./panel.js";
import { createPostFX } from "./postfx.js";
import { createPerfLogger } from "./perf.js";

function readWorks() {
  const el = document.getElementById("workData");
  if (!el) return null;
  try {
    const parsed = JSON.parse(el.textContent);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch {
    return null;
  }
}

function main() {
  window.__bgThree = {
    status: "starting",
    reducedMotion: prefersReducedMotion(),
    webgl: isWebGLAvailable(),
    modelsTotal: 0,
    modelsLoaded: 0
  };

  const works = readWorks();
  const spacer = document.getElementById("xpScrollSpace");
  if (!works || !spacer) return; // not the homepage

  if (prefersReducedMotion() || !isWebGLAvailable()) {
    window.__bgThree.status = "fallback";
    return; // static fallback stays visible
  }

  const tier = detectTier();

  /* ---------- renderer / scene ---------- */

  const canvas = document.createElement("canvas");
  canvas.className = "xp-canvas";
  canvas.setAttribute("aria-hidden", "true");
  document.body.prepend(canvas);

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false, // postfx pipeline; bloom hides aliasing
    powerPreference: "high-performance"
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, tier.pixelRatioCap));

  const scene = new THREE.Scene();
  window.__bgThree.scene = scene; // debug/testing hook
  scene.background = new THREE.Color(PALETTE.background);
  scene.fog = new THREE.FogExp2(PALETTE.background, tier.name === "mobile" ? 0.035 : 0.028);

  const camera = new THREE.PerspectiveCamera(
    55,
    window.innerWidth / Math.max(1, window.innerHeight),
    0.1,
    120
  );

  /* ---------- world ---------- */

  const { entries, yearMarkers } = layoutWorks(works);

  const tree = buildTree({
    targets: entries.map((e) => e.position),
    particleBudget: tier.branchParticles,
    seed: 20260702
  });
  scene.add(tree.points);

  const motes = buildMotes({ count: tier.motes, seed: 7 });
  scene.add(motes.points);

  // Floating models from the original site, hanging at branch tips
  // as particle clouds (they stream in asynchronously).
  const ornaments = buildOrnaments({
    tips: tree.tips,
    avoid: entries.map((e) => e.position),
    tier
  });
  scene.add(ornaments.group);

  // Ground-level grid plane and fossilized models among the roots.
  const underworld = buildUnderworld({ tier });
  scene.add(underworld.group);

  const worksLayer = buildWorks({
    entries,
    anchorFor: tree.anchorFor,
    orbitersPerWork: tier.orbitersPerWork
  });
  scene.add(worksLayer.group);

  const post = createPostFX({ renderer, scene, camera, tier });

  const perf = createPerfLogger({
    renderer,
    tier,
    getState: () => ({ mode, p: +smoothP.toFixed(3) })
  });

  /* ---------- camera path (keys along the descent) ---------- */

  const keys = [];
  keys.push({ y: WORLD.canopyTop + 2.5, az: entries[0].angle - 1.1, look: WORLD.canopyTop - 2 });
  for (const e of entries) keys.push({ y: e.y + 0.2, az: e.angle, look: e.y });
  keys.push({
    y: WORLD.rootsBottom + 2.0,
    az: entries[entries.length - 1].angle + 1.1,
    look: WORLD.rootsBottom + 1
  });

  // Scroll maps to the path by arc length so camera speed is constant
  // everywhere. Interpolation is strictly linear: any per-segment easing
  // makes the camera decelerate into each artwork, which reads as the
  // scroll "sticking". The global damping on smoothP provides all the
  // smoothing; centering on a work only happens on click/arrow-key focus.
  const segStarts = [0];
  {
    let total = 0;
    for (let i = 0; i < keys.length - 1; i++) {
      const dy = keys[i + 1].y - keys[i].y;
      // Azimuth sweep contributes travel at the camera's orbit radius.
      const daz = (keys[i + 1].az - keys[i].az) * WORLD.cameraRadius * 0.35;
      total += Math.max(0.001, Math.hypot(dy, daz));
      segStarts.push(total);
    }
    for (let i = 0; i < segStarts.length; i++) segStarts[i] /= total;
  }

  function sampleKeys(p) {
    const x = Math.max(0, Math.min(0.99999, p));
    let i = 0;
    while (i < segStarts.length - 2 && segStarts[i + 1] <= x) i++;
    const f = (x - segStarts[i]) / Math.max(1e-6, segStarts[i + 1] - segStarts[i]);
    const a = keys[i];
    const b = keys[i + 1];
    return {
      y: a.y + (b.y - a.y) * f,
      az: a.az + (b.az - a.az) * f,
      look: a.look + (b.look - a.look) * f
    };
  }

  /* ---------- scroll ---------- */

  const SCREENS_PER_STOP = 0.85;
  function scrollHeight() {
    return Math.round(keys.length * SCREENS_PER_STOP * window.innerHeight);
  }
  spacer.style.height = `${scrollHeight()}px`;

  let scrollP = 0; // raw target 0..1
  let smoothP = 0; // damped

  function readScroll() {
    exitFlight(); // any scroll hands control back to the descent
    const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    scrollP = Math.max(0, Math.min(1, window.scrollY / max));
  }
  window.addEventListener("scroll", readScroll, { passive: true });

  /* ---------- HUD ---------- */

  const canopyEl = document.getElementById("xpCanopy");
  const rootsEl = document.getElementById("xpRoots");
  const hintEl = document.getElementById("xpScrollHint");
  const navEl = document.getElementById("xpNav");

  const hud = document.querySelector(".xp-hud");

  // Year markers.
  const markerEls = yearMarkers.map((m) => {
    const el = document.createElement("div");
    el.className = "xp-year";
    el.textContent = m.year;
    const [r, g, b] = paletteAt(heightT(m.y));
    el.style.color = `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
    hud.appendChild(el);
    return { el, y: m.y, shown: false };
  });

  // Progress rail.
  const rail = document.createElement("div");
  rail.className = "xp-rail";
  const railDot = document.createElement("div");
  railDot.className = "xp-rail-dot";
  rail.appendChild(railDot);
  hud.appendChild(rail);

  // Dim veil behind the detail panel.
  const dim = document.createElement("div");
  dim.className = "xp-dim";
  document.body.appendChild(dim);

  // Hover tooltip.
  const tooltip = document.createElement("div");
  tooltip.className = "xp-tooltip";
  tooltip.innerHTML =
    '<p class="xp-tooltip-title"></p><p class="xp-tooltip-sub"></p>';
  document.body.appendChild(tooltip);
  const tooltipTitle = tooltip.querySelector(".xp-tooltip-title");
  const tooltipSub = tooltip.querySelector(".xp-tooltip-sub");

  /* ---------- picking ---------- */

  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2(-10, -10);
  let pointerClientX = 0;
  let pointerClientY = 0;
  let hoverIndex = -1;
  let pointerDirty = false;

  window.addEventListener(
    "pointermove",
    (e) => {
      pointer.set(
        (e.clientX / window.innerWidth) * 2 - 1,
        -(e.clientY / window.innerHeight) * 2 + 1
      );
      pointerClientX = e.clientX;
      pointerClientY = e.clientY;
      pointerDirty = true;
    },
    { passive: true }
  );

  /* ---------- detail mode ---------- */

  // mode: "journey" | "detail"
  let mode = "journey";
  let detailBlend = 0; // eased 0..1
  let detailTargetIndex = -1;
  let storedScrollY = 0;

  const panel = createPanel({
    onClose() {
      if (mode !== "detail") return;
      mode = "journey";
      window.scrollTo(0, storedScrollY);
    }
  });

  function scrollYForIndex(index) {
    const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    // Work `index` is centred at key `index + 1` along the path.
    return segStarts[index + 1] * max;
  }

  function openWork(index, { retarget = false } = {}) {
    const item = worksLayer.items[index];
    if (!item) return;
    const replaceHash = mode === "detail";
    if (mode === "journey") storedScrollY = window.scrollY;
    // When browsing work-to-work, closing should return to the newly
    // focused work's place in the descent, not where we first entered.
    if (retarget) storedScrollY = scrollYForIndex(index);
    mode = "detail";
    detailTargetIndex = index;
    panel.open(item.entry.work, { replaceHash });
  }

  function navigateWork(dir) {
    const n = worksLayer.items.length;
    if (!n) return;
    if (mode === "detail") {
      const next = detailTargetIndex + dir;
      // Phantom stops beyond both ends: browsing past the newest work
      // surfaces at the canopy, past the oldest sinks to the roots card.
      if (next < 0 || next >= n) {
        panel.close();
        window.scrollTo({
          top: next < 0 ? 0 : document.documentElement.scrollHeight,
          behavior: "smooth"
        });
        return;
      }
      openWork(next, { retarget: true });
    } else {
      // From the journey, arrows enter the browse flow at the work
      // nearest the current descent position.
      let nearest = 0;
      let bestD = Infinity;
      for (let i = 0; i < n; i++) {
        const d = Math.abs(smoothP - segStarts[i + 1]);
        if (d < bestD) {
          bestD = d;
          nearest = i;
        }
      }
      openWork(nearest, { retarget: true });
    }
  }

  window.addEventListener("keydown", (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    if (mode === "flight") return;
    e.preventDefault();
    navigateWork(e.key === "ArrowLeft" ? 1 : -1);
  });

  document.getElementById("xpPanelPrev")?.addEventListener("click", () => navigateWork(1));
  document.getElementById("xpPanelNext")?.addEventListener("click", () => navigateWork(-1));

  /* ---------- flight mode (WASD + mouse) ---------- */

  const flightKeys = { w: false, a: false, s: false, d: false };
  const flightPos = new THREE.Vector3();
  const flightVel = new THREE.Vector3();
  const flightTargetVel = new THREE.Vector3();
  const flightDir = new THREE.Vector3();
  const flightRight = new THREE.Vector3();
  const flightUp = new THREE.Vector3(0, 1, 0);
  let flightYaw = 0;
  let flightPitch = 0;

  function enterFlight() {
    if (mode !== "journey") return;
    mode = "flight";
    // Take over seamlessly from wherever the journey camera is.
    flightPos.copy(camera.position);
    camera.getWorldDirection(flightDir);
    flightYaw = Math.atan2(flightDir.x, flightDir.z);
    flightPitch = Math.asin(THREE.MathUtils.clamp(flightDir.y, -1, 1));
    flightVel.set(0, 0, 0);
  }

  function exitFlight() {
    if (mode !== "flight") return;
    mode = "journey";
    flightKeys.w = flightKeys.a = flightKeys.s = flightKeys.d = false;
  }

  window.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      exitFlight();
      return;
    }
    const k = e.key.toLowerCase();
    if (!(k in flightKeys) || e.metaKey || e.ctrlKey || e.altKey) return;
    if (mode === "detail") return;
    enterFlight();
    flightKeys[k] = true;
  });

  window.addEventListener("keyup", (e) => {
    const k = e.key.toLowerCase();
    if (k in flightKeys) flightKeys[k] = false;
  });

  // Scrolling hands control back to the descent.
  window.addEventListener("wheel", () => exitFlight(), { passive: true });

  function pickAtScreen(clientX, clientY) {
    const ndc = new THREE.Vector2(
      (clientX / window.innerWidth) * 2 - 1,
      -(clientY / window.innerHeight) * 2 + 1
    );
    raycaster.setFromCamera(ndc, camera);
    const hits = raycaster.intersectObjects(worksLayer.meshes, false);
    return hits.length ? hits[0].object.userData.workIndex : -1;
  }

  window.addEventListener("click", (e) => {
    if (e.target.closest(".xp-panel") || e.target.closest(".xp-overlay")) return;
    if (mode === "flight") {
      exitFlight();
      return;
    }
    if (mode === "detail") {
      panel.close();
      return;
    }
    // Synchronous pick so taps on mobile work immediately.
    const idx = hoverIndex >= 0 ? hoverIndex : pickAtScreen(e.clientX, e.clientY);
    if (idx >= 0) openWork(idx);
  });

  // Deep links: #/work/<slug>
  function slugFromHash() {
    const m = location.hash.match(/^#\/work\/(.+)$/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function applyHash({ jump = false } = {}) {
    const slug = slugFromHash();
    if (slug) {
      const index = worksLayer.items.findIndex((it) => it.entry.work.slug === slug);
      if (index >= 0) {
        if (jump) {
          // Position the journey at this work so closing feels right.
          window.scrollTo(0, scrollYForIndex(index));
          readScroll();
          smoothP = scrollP;
        }
        openWork(index);
      }
    } else if (mode === "detail") {
      panel.close({ silent: true });
    }
  }

  window.addEventListener("popstate", () => applyHash());

  /* ---------- resize / visibility ---------- */

  function resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    camera.aspect = w / Math.max(1, h);
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
    post.resize(w, h);
    spacer.style.height = `${scrollHeight()}px`;
    readScroll();
  }
  window.addEventListener("resize", resize, { passive: true });

  let running = true;
  document.addEventListener(
    "visibilitychange",
    () => {
      running = document.visibilityState === "visible";
    },
    { passive: true }
  );

  /* ---------- boot ---------- */

  document.body.classList.add("xp-active");
  resize();
  readScroll();
  smoothP = scrollP;
  applyHash({ jump: true });

  // Warm all shader programs while the canvas is still fading in, so
  // the first rendered frames don't stall on compilation (telemetry
  // showed a ~150ms hitch on every load without this).
  if (renderer.compileAsync) {
    renderer.compileAsync(scene, camera).catch(() => {});
  }

  const clock = new THREE.Clock();
  let t = 0;

  let hintShown = null;
  let railTopLast = null;

  const camPos = new THREE.Vector3();
  const lookAt = new THREE.Vector3();
  const workFocusCam = new THREE.Vector3();
  const workFocusLook = new THREE.Vector3();
  // Damped focus point so cycling works with the arrows sweeps the
  // camera around the tree instead of cutting to the next artwork.
  const focusCam = new THREE.Vector3();
  const focusLook = new THREE.Vector3();
  let focusLive = false;
  const outward = new THREE.Vector3();
  const panelShiftV = new THREE.Vector3();
  const projTmp = new THREE.Vector3();

  function tick() {
    if (!running) return;

    renderer.info.reset();

    const dt = Math.min(clock.getDelta(), 0.05);
    t += dt;

    // Hard-locked to the scrollbar: the camera is exactly where the
    // scroll position says it is, no smoothing lag.
    smoothP = scrollP;

    // Detail blend ease.
    const blendTarget = mode === "detail" ? 1 : 0;
    detailBlend += (blendTarget - detailBlend) * Math.min(1, dt * 3.4);

    // Journey camera.
    const k = sampleKeys(smoothP);
    if (mode === "flight") {
      // Mouse offset from screen centre steers; WASD moves.
      const dead = 0.12;
      const sx =
        Math.abs(pointer.x) > dead ? pointer.x - Math.sign(pointer.x) * dead : 0;
      const sy =
        Math.abs(pointer.y) > dead ? pointer.y - Math.sign(pointer.y) * dead : 0;
      flightYaw -= sx * dt * 1.9;
      flightPitch = Math.max(-1.35, Math.min(1.35, flightPitch + sy * dt * 1.4));

      const cosP = Math.cos(flightPitch);
      flightDir.set(
        Math.sin(flightYaw) * cosP,
        Math.sin(flightPitch),
        Math.cos(flightYaw) * cosP
      );
      flightRight.crossVectors(flightDir, flightUp).normalize();

      const fwd = (flightKeys.w ? 1 : 0) - (flightKeys.s ? 1 : 0);
      const strafe = (flightKeys.d ? 1 : 0) - (flightKeys.a ? 1 : 0);
      flightTargetVel
        .set(0, 0, 0)
        .addScaledVector(flightDir, fwd)
        .addScaledVector(flightRight, strafe);
      if (flightTargetVel.lengthSq() > 0) {
        flightTargetVel.normalize().multiplyScalar(5.5);
      }
      flightVel.lerp(flightTargetVel, Math.min(1, dt * 5));
      flightPos.addScaledVector(flightVel, dt);

      // Stay within the world: height band + a generous radius.
      flightPos.y = Math.max(
        WORLD.rootsBottom - 1.5,
        Math.min(WORLD.canopyTop + 6, flightPos.y)
      );
      const rr = Math.hypot(flightPos.x, flightPos.z);
      if (rr > 34) {
        flightPos.x *= 34 / rr;
        flightPos.z *= 34 / rr;
      }

      camPos.copy(flightPos);
      lookAt.copy(flightPos).add(flightDir);
    } else {
      const wobX = Math.sin(t * 0.22) * 0.35 + pointer.x * 0.7;
      const wobY = Math.cos(t * 0.19) * 0.3 + pointer.y * 0.45;
      camPos.set(
        Math.cos(k.az + wobX * 0.04) * WORLD.cameraRadius,
        k.y + wobY * 0.5,
        Math.sin(k.az + wobX * 0.04) * WORLD.cameraRadius
      );
      lookAt.set(0, k.look, 0);
    }

    // Detail camera (blend toward the focused work).
    if (detailTargetIndex >= 0 && detailBlend > 0.001) {
      const item = worksLayer.items[detailTargetIndex];
      const wp = item.node.position;
      outward.set(wp.x, 0, wp.z).normalize();
      const panelShift = window.innerWidth > 700 ? 0.9 : 0;
      panelShiftV.set(-outward.z * panelShift, 0.15, outward.x * panelShift);
      workFocusCam.copy(wp).addScaledVector(outward, 4.4).add(panelShiftV);
      workFocusLook.copy(wp);

      if (!focusLive) {
        // Entering detail from the journey: start at the target so the
        // detailBlend ease provides the whole move.
        focusCam.copy(workFocusCam);
        focusLook.copy(workFocusLook);
        focusLive = true;
      } else {
        // Already in detail: glide toward the (possibly new) work.
        const kf = Math.min(1, dt * 3.2);
        focusCam.lerp(workFocusCam, kf);
        focusLook.lerp(workFocusLook, kf);
      }

      const e = detailBlend * detailBlend * (3 - 2 * detailBlend);
      camPos.lerp(focusCam, e);
      lookAt.lerp(focusLook, e);
    } else {
      focusLive = false;
    }

    camera.position.copy(camPos);
    camera.lookAt(lookAt);

    // World updates.
    tree.update(t);
    motes.update(t);
    ornaments.update(t, dt);
    underworld.update(t);

    // Picking (journey mode only, when the pointer moved).
    if (mode === "journey" && pointerDirty) {
      pointerDirty = false;
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(worksLayer.meshes, false);
      const next = hits.length ? hits[0].object.userData.workIndex : -1;
      if (next !== hoverIndex) {
        hoverIndex = next;
        document.body.style.cursor = hoverIndex >= 0 ? "pointer" : "";
        if (hoverIndex >= 0) {
          const w = worksLayer.items[hoverIndex].entry.work;
          tooltipTitle.textContent = w.title;
          tooltipSub.textContent = [w.year, w.medium].filter(Boolean).join(" · ");
          tooltip.classList.add("is-visible");
        } else {
          tooltip.classList.remove("is-visible");
        }
      }
      if (hoverIndex >= 0) {
        tooltip.style.transform = `translate(${pointerClientX + 16}px, ${pointerClientY + 16}px)`;
      }
    } else if (mode !== "journey" && hoverIndex >= 0) {
      hoverIndex = -1;
      tooltip.classList.remove("is-visible");
      document.body.style.cursor = "";
    }

    worksLayer.update(t, camPos.y, mode === "detail" ? detailTargetIndex : hoverIndex, dt);

    dim.classList.toggle("is-visible", mode === "detail");
    window.__bgThree.mode = mode;

    // HUD visibility.
    const atCanopy = smoothP < 0.045 && mode === "journey";
    const atRoots = smoothP > 0.955 && mode === "journey";
    // Fade the site nav in once the visitor leaves the canopy intro.
    const navVisible = smoothP > 0.06 && mode === "journey";
    canopyEl.classList.toggle("is-visible", atCanopy);
    rootsEl.classList.toggle("is-visible", atRoots);
    if (navEl) navEl.classList.toggle("is-visible", navVisible);
    if (hintShown !== atCanopy) {
      hintShown = atCanopy;
      hintEl.style.opacity = atCanopy ? "" : "0";
    }

    const railTop = `${(smoothP * 100).toFixed(2)}%`;
    if (railTop !== railTopLast) {
      railTopLast = railTop;
      railDot.style.top = railTop;
    }

    // Year markers: project to screen. Style writes only on change.
    for (const m of markerEls) {
      let visible =
        mode === "journey" && Math.abs(m.y - k.y) < 5.5 && !atCanopy && !atRoots;
      if (visible) {
        // Place the label on the trunk, slightly camera-left.
        projTmp.set(0, m.y + 0.2, 0).project(camera);
        if (projTmp.z > 1) visible = false;
      }
      if (!visible) {
        if (m.shown) {
          m.shown = false;
          m.el.style.opacity = "0";
        }
        continue;
      }
      const sx = (projTmp.x * 0.5 + 0.5) * window.innerWidth;
      const sy = (-projTmp.y * 0.5 + 0.5) * window.innerHeight;
      if (!m.shown) {
        m.shown = true;
        m.el.style.opacity = "1";
      }
      m.el.style.transform = `translate(${Math.round(sx - 180)}px, ${Math.round(sy)}px)`;
    }

    // Render.
    post.update(t);
    post.composer.render();

    perf.frame();
  }

  renderer.setAnimationLoop(tick);
  window.__bgThree.status = "running";
}

try {
  main();
} catch (e) {
  window.__debugLastError = e?.message || String(e);
  window.__bgThree = { ...(window.__bgThree || {}), status: "error" };
}
